// ─────────────────────────────────────────────────────────────────────────────
// Idle animations from a single still image.
//
// The picture is cut into thin strips, and each strip is nudged by a wave:
//   • 'columns' (side-on fish): vertical strips move up and down, more toward
//     the tail, so the body undulates like it's swimming in place
//   • 'rows' (jellies, squid, crabs): horizontal strips move sideways and can
//     squeeze, so a bell pulses and tentacles trail
// On top of that the whole creature bobs and tilts a little.
//
// All positions are 0–1 across the creature's own outline (found from the
// image's transparent pixels on load), not the whole image file, so art with
// lots of empty space around it still lines up.
// ─────────────────────────────────────────────────────────────────────────────

/** A travelling wave over part of the body. */
export type Wave = {
  /** Where along the strips it acts (0 = left/top of the creature, 1 = right/bottom). */
  from: number;
  to: number;
  /** Strongest at `to` ('in'), at `from` ('out'), or the same throughout ('flat'). */
  ramp?: 'in' | 'out' | 'flat';
  /** Movement, as a share of the creature's height (columns) or width (rows). */
  amp: number;
  /** Seconds per cycle. */
  period: number;
  /** How many wavelengths fit across the creature; 0 = everything moves together. */
  waves?: number;
  phase?: number;
};

/** Rows only: squeeze the width of part of the body in and out (a jelly's bell). */
export type Pulse = { from: number; to: number; amount: number; period: number; phase?: number };

export type IdleProfile = {
  strips: 'columns' | 'rows';
  waves: Wave[];
  pulse?: Pulse;
  /** Whole-body float: share of height, seconds per cycle. */
  bob?: { amp: number; period: number };
  /** Whole-body rocking, degrees. */
  tilt?: { deg: number; period: number };
  /** Whole-body side-to-side drift (crabs scuttle), share of width. */
  sway?: { amp: number; period: number };
};

// Hand-tuned for each creature's anatomy (see the images in public/aquarium).
export const PROFILES: Record<string, IdleProfile> = {
  // Round body, tail fin at the right: the back half swishes.
  'hen-fish': {
    strips: 'columns',
    waves: [{ from: 0.5, to: 1, ramp: 'in', amp: 0.07, period: 1.1, waves: 0.8 }],
    bob: { amp: 0.025, period: 3.2 }, tilt: { deg: 2.5, period: 4.1 },
  },
  // Long body, big tail: a slow, wide swish from the middle back.
  'stern-flounder': {
    strips: 'columns',
    waves: [{ from: 0.4, to: 1, ramp: 'in', amp: 0.09, period: 1.6, waves: 0.9 }],
    bob: { amp: 0.02, period: 3.8 }, tilt: { deg: 1.5, period: 5 },
  },
  // Needle-thin: the whole body ripples, eel-like, gently at the snout.
  'wise-pointer': {
    strips: 'columns',
    waves: [
      { from: 0, to: 1, ramp: 'in', amp: 0.16, period: 1.4, waves: 1.3 },
      { from: 0, to: 0.25, ramp: 'out', amp: 0.04, period: 2.8, waves: 0 },
    ],
    bob: { amp: 0.03, period: 4.2 },
  },
  // Bell pulses; tentacles trail behind the pulse.
  'bobbing-ghost-jelly': {
    strips: 'rows',
    pulse: { from: 0, to: 0.62, amount: 0.07, period: 1.8 },
    waves: [{ from: 0.55, to: 1, ramp: 'in', amp: 0.09, period: 1.8, waves: 0.7, phase: 0.6 }],
    bob: { amp: 0.06, period: 1.8 },
  },
  // Mantle leans one way while the tentacles ripple the other.
  'toe-squid': {
    strips: 'rows',
    waves: [
      { from: 0, to: 0.3, ramp: 'out', amp: 0.04, period: 2.6, waves: 0 },
      { from: 0.62, to: 1, ramp: 'in', amp: 0.08, period: 1.2, waves: 1.2, phase: Math.PI },
    ],
    pulse: { from: 0.22, to: 0.66, amount: 0.025, period: 2.6 },
    bob: { amp: 0.035, period: 2.6 },
  },
  // Sprout sways, legs shuffle quickly, the body bobs and scuttles sideways.
  'island-scuttle-crab': {
    strips: 'rows',
    waves: [
      { from: 0, to: 0.16, ramp: 'out', amp: 0.06, period: 2.2, waves: 0 },
      { from: 0.68, to: 1, ramp: 'in', amp: 0.035, period: 0.45, waves: 1.5 },
    ],
    pulse: { from: 0.15, to: 0.7, amount: 0.02, period: 0.9 },
    bob: { amp: 0.015, period: 0.9 }, sway: { amp: 0.05, period: 3.6 },
  },
};

/** A gentle default for any creature without its own profile. */
export const DEFAULT_PROFILE: IdleProfile = {
  strips: 'columns',
  waves: [{ from: 0.5, to: 1, ramp: 'in', amp: 0.05, period: 1.4, waves: 0.8 }],
  bob: { amp: 0.025, period: 3.4 },
};

const TAU = Math.PI * 2;

function weight(w: Wave, u: number): number {
  if (u < w.from || u > w.to) return 0;
  const k = w.to === w.from ? 1 : (u - w.from) / (w.to - w.from);
  const r = w.ramp ?? 'in';
  // Smoothstep so the moving part blends into the still part with no kink.
  const s = (x: number) => x * x * (3 - 2 * x);
  return r === 'flat' ? 1 : r === 'in' ? s(k) : s(1 - k);
}

function waveAt(waves: Wave[], u: number, t: number): number {
  let d = 0;
  for (const w of waves) {
    const wt = weight(w, u);
    if (!wt) continue;
    d += wt * w.amp * Math.sin(TAU * (t / w.period - (w.waves ?? 0) * u) + (w.phase ?? 0));
  }
  return d;
}

type Box = { x: number; y: number; w: number; h: number };

/** The creature's outline inside the image: the box around pixels that aren't (nearly) transparent. */
function contentBox(img: HTMLImageElement): Box {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const { data, width, height } = g.getImageData(0, 0, c.width, c.height);
  let x0 = width, y0 = height, x1 = -1, y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 16) {
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  return x1 < 0 ? { x: 0, y: 0, w: width, h: height } : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Just the creature, cropped to its outline, with its longest side `maxSide` px. Draw it with drawIdle. */
export type Sprite = { canvas: HTMLCanvasElement; w: number; h: number };

export function makeSprite(img: HTMLImageElement, maxSide: number): Sprite {
  const box = contentBox(img);
  const k = maxSide / Math.max(box.w, box.h);
  const w = Math.max(1, Math.round(box.w * k)), h = Math.max(1, Math.round(box.h * k));
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const g = canvas.getContext('2d')!;
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, w, h);
  return { canvas, w, h };
}

export type DrawOptions = {
  /** Centre of the creature, in canvas pixels. */
  x: number;
  y: number;
  /** Drawn width; the height follows the sprite's shape. */
  width: number;
  /** Which way it faces: 1 = as drawn, -1 = mirrored. */
  facing?: 1 | -1;
  /** Strip width in canvas pixels (about 2 CSS px). */
  step?: number;
};

/** Draw one frame of a creature's idle animation at time `t` (seconds). */
export function drawIdle(ctx: CanvasRenderingContext2D, s: Sprite, p: IdleProfile, t: number, o: DrawOptions): void {
  const dw = o.width, dh = (o.width * s.h) / s.w;
  const step = Math.max(1, Math.round(o.step ?? 2));
  const bob = p.bob ? p.bob.amp * dh * Math.sin(TAU * t / p.bob.period) : 0;
  const sway = p.sway ? p.sway.amp * dw * Math.sin(TAU * t / p.sway.period) : 0;
  const tilt = p.tilt ? (p.tilt.deg * Math.PI / 180) * Math.sin(TAU * t / p.tilt.period) : 0;
  const facing = o.facing ?? 1;

  ctx.save();
  ctx.translate(o.x + sway, o.y + bob);
  if (tilt) ctx.rotate(tilt);
  if (facing === -1) ctx.scale(-1, 1);
  ctx.translate(-dw / 2, -dh / 2);
  const kx = s.w / dw, ky = s.h / dh;

  // Each strip overlaps the next by a pixel so no seams show.
  if (p.strips === 'columns') {
    for (let x = 0; x < dw; x += step) {
      const u = (x + step / 2) / dw;
      const dy = waveAt(p.waves, u, t) * dh;
      const w = Math.min(step + 1, dw - x);
      ctx.drawImage(s.canvas, x * kx, 0, w * kx, s.h, x, dy, w, dh);
    }
  } else {
    for (let y = 0; y < dh; y += step) {
      const u = (y + step / 2) / dh;
      const dx = waveAt(p.waves, u, t) * dw;
      let k = 1;
      if (p.pulse && u >= p.pulse.from && u <= p.pulse.to) {
        const m = (u - p.pulse.from) / (p.pulse.to - p.pulse.from);
        k = 1 - p.pulse.amount * Math.sin(Math.PI * m) * (0.5 + 0.5 * Math.sin(TAU * t / p.pulse.period + (p.pulse.phase ?? 0)));
      }
      const h = Math.min(step + 1, dh - y);
      const rw = dw * k;
      ctx.drawImage(s.canvas, 0, y * ky, s.w, h * ky, (dw - rw) / 2 + dx, y, rw, h);
    }
  }
  ctx.restore();
}

export const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export type IdleOptions = {
  profile?: IdleProfile;
  /** Mirror it (to swim the other way). */
  flip?: boolean;
  /** Start somewhere else in the cycle, so a tank of the same fish doesn't move in lockstep. */
  offset?: number;
  /** Speed multiplier. */
  speed?: number;
};

/**
 * Animate one creature in its own canvas until the returned function is
 * called. The canvas is sized to its CSS box and the creature fitted inside
 * with room to move. Pauses while off screen; holds still for reduced motion.
 */
export function startIdle(canvas: HTMLCanvasElement, img: HTMLImageElement, opts: IdleOptions = {}): () => void {
  const p = opts.profile ?? DEFAULT_PROFILE;
  const ctx = canvas.getContext('2d')!;
  const reduced = prefersReducedMotion();
  const speed = opts.speed ?? 1;
  const offset = opts.offset ?? Math.random() * 10;

  let sprite: Sprite | null = null;
  let W = 0, H = 0, dpr = 1, width = 0;
  const layout = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const r = canvas.getBoundingClientRect();
    W = Math.max(1, Math.round(r.width * dpr)); H = Math.max(1, Math.round(r.height * dpr));
    canvas.width = W; canvas.height = H;
    sprite = makeSprite(img, Math.max(W, H));
    // Leave a margin for the bob, tilt and waves.
    const k = Math.min((W * 0.82) / sprite.w, (H * 0.82) / sprite.h);
    width = sprite.w * k;
  };
  layout();

  const draw = (now: number) => {
    if (!sprite) return;
    const t = reduced ? 0 : (now / 1000) * speed + offset;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, W, H);
    drawIdle(ctx, sprite, p, t, { x: W / 2, y: H / 2, width, facing: opts.flip ? -1 : 1, step: 2 * dpr });
  };

  let raf = 0;
  let visible = true;
  const loop = (now: number) => { draw(now); if (!reduced && visible) raf = requestAnimationFrame(loop); };
  raf = requestAnimationFrame(loop);

  const io = new IntersectionObserver(([e]) => {
    const was = visible;
    visible = e.isIntersecting;
    if (visible && !was && !reduced) raf = requestAnimationFrame(loop);
    if (!visible) cancelAnimationFrame(raf);
  });
  io.observe(canvas);
  const ro = new ResizeObserver(() => { layout(); draw(performance.now()); });
  ro.observe(canvas);

  return () => { cancelAnimationFrame(raf); io.disconnect(); ro.disconnect(); };
}
